import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Coins, Filter, Mic2, Radio, Wallet } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useMe } from "@/context/MeContext";

const HERO = "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=2000&q=80";
const perks = [
  [Coins, "Cobras por escuchar", "Recibes el 70% de cada envío: entre USD 1,40 y USD 4,20 por canción, según el precio que elijas."],
  [Filter, "Música filtrada para ti", "Solo te llegan canciones de los géneros que tú defines. Nada de bandejas llenas de spam."],
  [Radio, "Tu programa en nuestra radio", "Puedes presentar programas en vivo en RARA PERLA y llevar oyentes a tu medio."],
  [BadgeCheck, "Insignia de verificado", "Los medios que responden a tiempo se destacan en el directorio y reciben más envíos."],
];

function Calculator() {
  const [sends, setSends] = useState(30);
  const [cost, setCost] = useState(2);
  const monthly = sends * cost * 2 * 0.7;
  return (
    <div data-testid="earnings-calculator" className="rounded-2xl border border-white/10 bg-[#12121A] p-8">
      <p className="font-mono text-xs tracking-widest text-amber-500/90">CALCULA TUS INGRESOS</p>
      <p className="mt-6 text-sm text-slate-400">Envíos que escuchas por mes: <span data-testid="calc-sends" className="font-mono text-white">{sends}</span></p>
      <Slider data-testid="calc-sends-slider" className="mt-3" value={[sends]} min={5} max={200} step={5} onValueChange={([v]) => setSends(v)} />
      <p className="mt-6 text-sm text-slate-400">Precio por envío</p>
      <div className="mt-3 flex gap-2">
        {[1, 2, 3].map((c) => (
          <button key={c} data-testid={`calc-cost-${c}`} onClick={() => setCost(c)} className={`flex-1 rounded-full border py-2 text-sm transition-colors ${cost === c ? "border-[#FF4500] bg-[#FF4500]/15 text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>
            {c} crédito{c > 1 ? "s" : ""}
          </button>
        ))}
      </div>
      <p className="mt-8 font-mono text-xs text-slate-500">GANARÍAS AL MES</p>
      <p data-testid="calc-result" className="mt-1 font-heading text-5xl font-extrabold text-[#FF4500]">USD {monthly.toFixed(2)}</p>
      <p className="mt-2 text-xs text-slate-500">1 crédito = USD 2 · tú recibes el 70% (USD {(cost * 1.4).toFixed(2)} por envío)</p>
    </div>
  );
}

function JoinButton({ testId }) {
  const { me } = useMe();
  const to = !me ? "/sign-up?rol=medio" : !me.onboarded ? "/bienvenida?rol=curator" : "/panel";
  const label = me?.onboarded ? "Ir a mi panel" : "Sumar mi medio";
  return (
    <Link to={to} data-testid={testId} className="inline-flex items-center gap-2 rounded-full bg-[#FF4500] px-7 py-3.5 font-semibold text-white transition-transform hover:scale-[1.03]">
      {label} <ArrowRight className="h-4 w-4" />
    </Link>
  );
}

export default function JoinMedia() {
  return (
    <div data-testid="join-media-page">
      <section className="relative flex min-h-[70vh] items-end overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-[#0A0A0E]/75 to-[#0A0A0E]/30" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 pb-16 md:px-8">
          <p className="fade-up font-mono text-xs tracking-widest text-amber-500/90">PARA RADIOS, PODCASTS Y BLOGS</p>
          <h1 className="fade-up mt-5 max-w-4xl font-heading text-4xl font-extrabold leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-7xl" style={{ animationDelay: "80ms" }}>
            Cobra por descubrir <span className="text-[#FF4500]">nueva música</span>.
          </h1>
          <p className="fade-up mt-6 max-w-xl text-base text-slate-300 md:text-lg" style={{ animationDelay: "160ms" }}>
            Artistas independientes de Iberoamérica pagan para que escuches su canción y les des tu opinión. Tú te quedas con el 70% de cada envío.
          </p>
          <div className="fade-up mt-10" style={{ animationDelay: "240ms" }}><JoinButton testId="join-media-hero-btn" /></div>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1400px] gap-12 px-4 py-24 md:px-8 lg:grid-cols-2">
        <div>
          <h2 className="font-heading text-3xl font-bold text-white sm:text-4xl">¿Cuánto ganas?</h2>
          <p className="mt-4 max-w-md text-slate-400">Tú eliges cuánto cuesta enviarte una canción (de 1 a 3 créditos). Escuchas, respondes "Emitida" o "No emitida" con tu opinión y el saldo se suma a tu panel.</p>
          <div className="mt-10 grid gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 sm:grid-cols-2">
            {perks.map(([Icon, t, d]) => (
              <div key={t} className="bg-[#0A0A0E] p-6">
                <Icon className="h-5 w-5 text-[#FF4500]" />
                <h3 className="mt-3 font-heading text-lg font-bold text-white">{t}</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-400">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <Calculator />
      </section>

      <section className="border-y border-white/5 bg-[#12121A]">
        <div className="mx-auto max-w-[1400px] px-4 py-24 md:px-8">
          <h2 className="font-heading text-3xl font-bold text-white sm:text-4xl">Súmate en dos pasos</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {[[Mic2, "01", "Crea tu cuenta", "Con tu email y una contraseña. Tarda menos de un minuto."],
              [Wallet, "02", "Completa los datos de tu medio", "Nombre, géneros, país, formato y precio por envío. Apareces en el directorio al instante."]].map(([Icon, n, t, d]) => (
              <div key={n} className="rounded-xl border border-white/10 bg-[#0A0A0E] p-8">
                <div className="flex items-center gap-3"><span className="font-mono text-sm text-[#FF4500]">{n}</span><Icon className="h-5 w-5 text-slate-500" /></div>
                <h3 className="mt-4 font-heading text-xl font-bold text-white">{t}</h3>
                <p className="mt-2 text-sm text-slate-400">{d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10"><JoinButton testId="join-media-bottom-btn" /></div>
          <p className="mt-6 max-w-2xl text-xs text-slate-500">Tienes 7 días para responder cada envío; si no respondes, el artista recupera sus créditos. El saldo ganado se muestra en tu panel y el retiro estará disponible próximamente.</p>
        </div>
      </section>
    </div>
  );
}
